import { useTranslation } from "react-i18next";
import { Badge } from "../Badge/Badge";
import { Button } from "../Button/Button";
import { describeSyncError, syncErrorSummary } from "../../../lib/sync-error-text";

interface SapSyncStatusProps {
	sapDocNum: number | null | undefined;
	sapDocEntry: number | null | undefined;
	syncStatus: string | null | undefined;
	syncError: string | null | undefined;
	/** Belge "muhatabın SAP kartı yok" diye bekliyorsa, muhatabın SAP'a neden aktarılamadığı. */
	partnerError?: string | null | undefined;
	retrying: boolean;
	onRetry: () => void;
	numberShownElsewhere?: boolean;
	/** Belge potansiyel müşteriye bağlı ve muhatabı yok: SAP'a hiç gönderilmez, "bekliyor" yanıltıcı olur. */
	leadOnly?: boolean;
}

/**
 * Belgenin SAP durumunu gösterir. SAP'te karşılığı olan bir belgede sonraki bir güncelleme
 * gönderilemediyse yeşil "SAP #..." rozeti tek başına yanıltıcı olur — bu durumda kayıt CRM ile
 * SAP arasında farklıdır, o yüzden hata rozeti ve tekrar gönder düğmesi de gösterilir.
 */
export function SapSyncStatus({
	sapDocNum,
	sapDocEntry,
	syncStatus,
	syncError,
	partnerError,
	retrying,
	onRetry,
	numberShownElsewhere = false,
	leadOnly = false,
}: SapSyncStatusProps) {
	const { t } = useTranslation();
	const inSap = sapDocNum ?? sapDocEntry;
	if (leadOnly && inSap == null) {
		return (
			<span title={t("leadOfferNotSentToSap")}>
				<Badge variant="muted">{t("sapLeadNotSent")}</Badge>
			</span>
		);
	}
	const needsAttention = syncStatus === "manual_review";
	const isFailed = syncStatus === "failed" || needsAttention;

	if (!isFailed) {
		if (inSap != null)
			return (
				<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
					{!numberShownElsewhere && <Badge variant="success">SAP #{inSap}</Badge>}
					<Button size="sm" variant="secondary" disabled={retrying} onClick={onRetry}>
						{t("sapRetry")}
					</Button>
				</div>
			);
		if (syncStatus === "in_progress") return <Badge variant="warning">{t("sapSyncing")}</Badge>;
		return (
			<div style={{ display: "flex", alignItems: "center", gap: 6 }}>
				<Badge variant="warning">{t("sapPending")}</Badge>
				<Button size="sm" variant="secondary" disabled={retrying} onClick={onRetry}>
					{t("sapRetry")}
				</Button>
			</div>
		);
	}

	return (
		<div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
			<div title={describeSyncError(syncError, t)}>
				{inSap != null && (
					<div style={{ marginBottom: 2 }}>
						<Badge variant="muted">SAP #{inSap}</Badge>
					</div>
				)}
				<Badge variant="danger">
					{needsAttention
						? t("sapNeedsAttention")
						: inSap != null
							? t("sapUpdateNotSent")
							: t("sapNotSent")}
				</Badge>
				{syncError && (
					<div
						style={{
							fontSize: 11,
							color: "var(--color-danger, #c0392b)",
							maxWidth: 260,
							marginTop: 2,
							whiteSpace: "normal",
						}}
					>
						{syncErrorSummary(syncError, t)}
						{partnerError && (
							<div style={{ marginTop: 2 }}>
								{t("sapPartnerError")}: {partnerError}
							</div>
						)}
					</div>
				)}
			</div>
			<Button size="sm" variant="secondary" disabled={retrying} onClick={onRetry}>
				{t("sapRetry")}
			</Button>
		</div>
	);
}
